/**
 * Preset schema — the JSON shape persisted to IndexedDB and exported to disk.
 *
 * Versioning is the single most important invariant: bump `schemaVersion`
 * whenever this shape changes, and add a step to `migrations.ts` so older
 * stored presets keep loading. The migration chain is mandatory, not
 * optional — even a trivial rename benefits from going through the same
 * pipeline as a structural change.
 */

import type { ModConnection } from '@ac-slayer/synth-core';
import type { ModuleState } from '@ac-slayer/synth-core';
import type { VoiceMode } from '@ac-slayer/synth-core';

/** Increment on ANY structural change to the preset shape below. */
export const CURRENT_SCHEMA_VERSION = 5;

/** Fallback category for presets that predate the category field (v1). */
export const DEFAULT_PRESET_CATEGORY = 'Uncategorized';

export interface PresetVoiceSettings {
  readonly polyphony: number;
  readonly mode: VoiceMode;
  readonly glideMs: number;
  /**
   * Voices per note in POLY (and per-note stack depth in MONO/LEG).
   * Optional for back-compat with v1 presets that pre-date unison; missing
   * values default to 1 on load.
   */
  readonly unison?: number;
  /** Detune spread (cents) between unison voices. Defaults to 15 when absent. */
  readonly unisonSpread?: number;
}

/**
 * One effect instance saved into a preset. The runtime rebuilds the
 * effects chain from this list — same order, same ids. Connections in
 * `modConnections` reference effects by `id`, so loading the preset
 * keeps mod wiring intact.
 *
 * Introduced in v4. Pre-v4 presets had effects mixed into the `modules`
 * list and ordered via the parallel `effectsOrder` array; the v3→v4
 * migration extracts them into this shape.
 */
export interface PresetEffectInstance {
  readonly type: string;
  readonly id: string;
  readonly bypass?: boolean;
  readonly params: Record<string, number | string>;
}

export interface Preset {
  readonly schemaVersion: number;
  readonly id: string;
  readonly name: string;
  readonly author?: string;
  readonly createdAt: number;
  readonly updatedAt: number;
  /**
   * Short category label used to group presets in the browser ("Bass",
   * "Lead", "Pad", user-defined). Always present as of v2; older presets
   * get DEFAULT_PRESET_CATEGORY on migration.
   */
  readonly category: string;
  readonly tags: readonly string[];
  /** Transport BPM at save time. */
  readonly bpm: number;
  readonly voice: PresetVoiceSettings;
  /**
   * State for non-effect modules: OSC, FILT, ENV, VCA, LFO, S&H, NOISE,
   * ARP. Effects live in their own `effects` list as of v4. Older
   * presets had effects mixed in here; the v3→v4 migration sorts them.
   */
  readonly modules: readonly ModuleState[];
  /** All mod connections active at save time. */
  readonly modConnections: readonly ModConnection[];
  /**
   * Macro slider values (0..1) in bank order. Optional for back-compat with
   * presets saved before macros existed; missing field means "don't touch
   * current macro values on load."
   */
  readonly macros?: readonly number[];
  /**
   * Order of effect modules in the post-voice chain (by module type —
   * e.g. `['delay', 'distortion', 'reverb', 'bitcrusher']`). Missing
   * unknown types are appended in their default position on load.
   * Added in v3; superseded by `effects` in v4 but still emitted for
   * forward-compat with older clients.
   */
  readonly effectsOrder?: readonly string[];
  /**
   * Dynamic effect chain — full list of effect instances in chain
   * order. Each entry has its own id so the chain can hold multiple
   * instances of the same type (two delays, three distortions). Added
   * in v4. The v3→v4 migration builds this from the prior `modules` /
   * `effectsOrder` shape.
   */
  readonly effects?: readonly PresetEffectInstance[];
  /**
   * MIDI Learn bindings active at save time. Each entry maps a
   * (channel, controller) pair to a paramRef like
   * `module:filter:Cutoff`. Added in v5. Pre-v5 presets get an empty
   * array on migration so the runtime doesn't try to apply
   * undefined.
   */
  readonly midiBindings?: readonly PresetMidiBinding[];
}

/** A single MIDI Learn mapping persisted with a preset. */
export interface PresetMidiBinding {
  readonly paramRef: string;
  readonly channel: number;
  readonly controller: number;
}

/** Shorthand for mint-time fields — id/timestamps generated at save time. */
export type PresetCapture = Omit<
  Preset,
  | 'id'
  | 'createdAt'
  | 'updatedAt'
  | 'name'
  | 'tags'
  | 'author'
  | 'category'
  | 'schemaVersion'
>;

export function mintPresetId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return `preset-${crypto.randomUUID().slice(0, 8)}`;
  }
  return `preset-${Math.random().toString(36).slice(2, 10)}`;
}
